/**
 * BlogTemplateRoute — dispatch shell for the 4 marketing-shell blog
 * page components.
 *
 * Phase 28 K.2 (2026-05-18). Wired into `createMarketingApp` as the
 * branch for routes whose `template` starts with `blog-`. Reads the
 * URL params via `useParams()` and renders the matching blog page
 * with the configured `cmsClient` + the right param prop:
 *
 *   template='blog-index'    → <BlogIndexPage client={cmsClient} />
 *   template='blog-post'     → <BlogPostPage client locale slug={params.slug} />
 *   template='blog-category' → <BlogCategoryPage client categorySlug={params.category} />
 *   template='blog-tag'      → <BlogTagPage client tagSlug={params.tag} />
 *
 * The URL param NAME (`:slug` vs `:category` vs `:tag`) is the
 * canonical convention every consumer app honours. K.4 wires the same
 * 4 routes into all 8 web apps via the factory.
 *
 * @module @puffin-core/marketing-shell/blog/BlogTemplateRoute
 */

import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import BlogIndexPage from './BlogIndexPage.jsx';
import BlogPostPage from './BlogPostPage.jsx';
import BlogCategoryPage from './BlogCategoryPage.jsx';
import BlogTagPage from './BlogTagPage.jsx';
import { getBlogUiStrings } from './blogUiStrings.js';
import { readBlogSsrSeed, clearBlogSsrSeed } from './readBlogSsrSeed.js';

const BLOG_TEMPLATES = Object.freeze(['blog-index', 'blog-post', 'blog-category', 'blog-tag']);

/**
 * @param {Object} props
 * @param {'blog-index'|'blog-post'|'blog-category'|'blog-tag'} props.template
 * @param {Object} props.cmsClient
 * @param {string} [props.locale]
 * @param {string} [props.hrefPrefix='/blog']
 */
export default function BlogTemplateRoute({ template, cmsClient, locale, hrefPrefix = '/blog' }) {
  if (!BLOG_TEMPLATES.includes(template)) {
    throw new TypeError(
      `BlogTemplateRoute: template "${template}" is not one of ${BLOG_TEMPLATES.join(', ')}`,
    );
  }
  const params = useParams();
  // BLOG-STD-001: resolve the localized chrome strings from the ecosystem SSOT
  // and thread them into the page components. The prerender harness
  // (renderBlogHtml) resolves the SAME strings for the SAME locale, so the
  // server-rendered HTML and this client render emit byte-identical chrome.
  const strings = getBlogUiStrings(locale);

  // BLOG-STD-001 full-shell SSR: on the hydration render, reproduce the server's
  // READY-branch markup by seeding the page component's `initial*` props from
  // the `<script type="application/json" id="__blog-ssr-seed__">` data block
  // (route-matched). PURE read — safe during render. The seed is freed in a
  // post-commit effect so a later client navigation never re-reads it. Absent /
  // non-matching seed → byte-identical to prior behavior.
  const ssrInitial = readBlogSsrSeed({ template, params }) || {};
  useEffect(() => {
    clearBlogSsrSeed();
  }, []);

  if (template === 'blog-index') {
    // WS-J — `/blog/page/:page`. Placed BEFORE the seed spread so an SSR-seeded
    // `initialOffset` still wins; this only carries an in-SPA deep link, where
    // the seed has already been cleared.
    const parsedPage = Number.parseInt(params.page, 10);
    return (
      <BlogIndexPage
        client={cmsClient}
        locale={locale}
        hrefPrefix={hrefPrefix}
        strings={strings}
        initialPage={Number.isFinite(parsedPage) && parsedPage > 1 ? parsedPage : 1}
        {...ssrInitial}
      />
    );
  }
  if (template === 'blog-post') {
    return (
      <BlogPostPage
        client={cmsClient}
        slug={params.slug}
        locale={locale}
        hrefPrefix={hrefPrefix}
        strings={strings}
        {...ssrInitial}
      />
    );
  }
  if (template === 'blog-category') {
    return (
      <BlogCategoryPage
        client={cmsClient}
        categorySlug={params.category}
        locale={locale}
        hrefPrefix={hrefPrefix}
        strings={strings}
        {...ssrInitial}
      />
    );
  }
  // template === 'blog-tag' (the only remaining option per the enum check above).
  return (
    <BlogTagPage
      client={cmsClient}
      tagSlug={params.tag}
      locale={locale}
      hrefPrefix={hrefPrefix}
      strings={strings}
      {...ssrInitial}
    />
  );
}

BlogTemplateRoute.propTypes = {
  template: PropTypes.oneOf(BLOG_TEMPLATES).isRequired,
  cmsClient: PropTypes.shape({
    listBlogPosts: PropTypes.func.isRequired,
    resolveBlogPost: PropTypes.func.isRequired,
    listBlogCategories: PropTypes.func.isRequired,
    listBlogTags: PropTypes.func.isRequired,
  }).isRequired,
  locale: PropTypes.string,
  hrefPrefix: PropTypes.string,
};

BlogTemplateRoute.BLOG_TEMPLATES = BLOG_TEMPLATES;
